/**
 * EmailSenderDomainSection (Story 15.4 — Custom Email Sender Domain, Agency)
 *
 * Self-contained Agency-only settings sub-panel for the white-label report
 * sender domain. It manages its OWN data (react-query against the
 * /api/tenant/branding/email-sender endpoints) rather than the shared branding
 * form, because the domain has an independent verify/OCC lifecycle:
 *   - enter a sender domain → SES Easy-DKIM provisions → we show the 3 CNAME
 *     records the agency must publish in DNS (the "guides me through DNS" AC);
 *   - a status badge reflects pending/verifying/verified/failed;
 *   - "Check verification" re-polls SES on demand (a cron also auto-polls);
 *   - an optional reply-to support email.
 *
 * Non-agency plans see an upgrade hint (mirrors the portal-domain shelf).
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label } from "@provenleap/ui";
import type { Plan } from "@provenleap/types";
import {
  fetchEmailSender,
  updateEmailSender,
  verifyEmailSender,
  TenantBrandingConflictError,
  TenantBrandingValidationError,
  type EmailSenderProjection,
  type EmailDomainStatus,
} from "../../lib/tenantBrandingApi";

const QUERY_KEY = ["tenant", "email-sender"] as const;

const STATUS_LABEL: Record<EmailDomainStatus, string> = {
  pending: "Pending DNS",
  verifying: "Verifying…",
  verified: "Verified",
  failed: "Verification failed",
};

const STATUS_CLASS: Record<EmailDomainStatus, string> = {
  pending: "border-amber-300 text-amber-900 bg-amber-50",
  verifying: "border-amber-300 text-amber-900 bg-amber-50",
  verified: "border-emerald-300 text-emerald-900 bg-emerald-50",
  failed: "border-red-300 text-red-900 bg-red-50",
};

function StatusBadge({ status }: { status: EmailDomainStatus }) {
  return (
    <span
      data-testid="email-domain-status"
      className={`inline-flex items-center rounded-full border-[0.5px] px-2.5 py-0.5 text-[11px] font-medium ${STATUS_CLASS[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export function EmailSenderDomainSection({ plan }: { plan: Plan }) {
  // White-label is a paid feature (all tiers except the free "Solo" plan).
  const isPaid = plan !== "free";

  if (!isPaid) {
    return (
      <div className="space-y-2">
        <Label htmlFor="emailSenderDomain">Custom email sender domain</Label>
        <Input id="emailSenderDomain" disabled value="" placeholder="mail.youragency.com" />
        <p className="text-[12px] text-muted-foreground">
          Send report emails from your own domain — available on every paid plan.{" "}
          <Link to="/settings/billing" className="underline underline-offset-2">
            Upgrade your plan
          </Link>
        </p>
      </div>
    );
  }

  return <EmailSenderDomainForm />;
}

function EmailSenderDomainForm() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchEmailSender,
  });

  const [domain, setDomain] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Seed the local inputs ONCE from the first server projection. We deliberately
  // do NOT re-sync both inputs on every `data` change: a save/verify updates the
  // cached projection (changing `data`), and a blanket re-sync would clobber an
  // unsaved edit in the OTHER field. Post-save re-sync is surgical (only the
  // patched field) inside each mutation's onSuccess. Mirrors branding.tsx.
  const seededRef = useRef(false);
  useEffect(() => {
    if (data && !seededRef.current) {
      setDomain(data.customEmailDomain ?? "");
      setSupportEmail(data.supportEmail ?? "");
      seededRef.current = true;
    }
  }, [data]);

  function onSettled(next: EmailSenderProjection) {
    queryClient.setQueryData(QUERY_KEY, next);
  }

  function handleError(err: unknown) {
    if (err instanceof TenantBrandingValidationError) {
      setError(err.fields[0]?.message ?? err.message);
    } else if (err instanceof TenantBrandingConflictError) {
      setError(
        "These settings changed in another tab. Reloaded — please re-apply your change.",
      );
      // Allow the refetched authoritative state to re-seed the form.
      seededRef.current = false;
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    } else {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  const saveMutation = useMutation({
    mutationFn: (patch: { customEmailDomain?: string | null; supportEmail?: string | null }) =>
      updateEmailSender({ ...patch, updatedAt: data!.updatedAt }),
    onSuccess: (next, patch) => {
      setError(null);
      setNotice("Saved.");
      // Re-sync ONLY the field this patch actually wrote — never touch the other
      // input, which may hold an unsaved edit.
      if ("customEmailDomain" in patch) setDomain(next.customEmailDomain ?? "");
      if ("supportEmail" in patch) setSupportEmail(next.supportEmail ?? "");
      onSettled(next);
    },
    onError: (err) => {
      setNotice(null);
      handleError(err);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: verifyEmailSender,
    onSuccess: (next) => {
      setError(null);
      setNotice(
        next.customEmailDomainStatus === "verified"
          ? "Domain verified."
          : "Still waiting on DNS — DKIM can take up to 72 hours to propagate.",
      );
      // Verify writes neither input field — update the cache only, leave both
      // inputs (and any unsaved edits) untouched.
      onSettled(next);
    },
    onError: (err) => {
      setNotice(null);
      handleError(err);
    },
  });

  if (isLoading) {
    return <p className="text-[12px] text-muted-foreground">Loading email settings…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-[12px] text-red-900">
        Could not load email sender settings. Please refresh.
      </p>
    );
  }

  const status = data.customEmailDomainStatus;
  const hasDomain = Boolean(data.customEmailDomain);
  const busy = saveMutation.isPending || verifyMutation.isPending;
  const trimmedDomain = domain.trim();
  const domainDirty = trimmedDomain !== (data.customEmailDomain ?? "");
  const supportDirty = supportEmail.trim() !== (data.supportEmail ?? "");

  return (
    <div className="space-y-4">
      {/* Sender domain */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="emailSenderDomain">Custom email sender domain</Label>
          {hasDomain && status ? <StatusBadge status={status} /> : null}
        </div>
        <div className="flex items-center gap-2">
          <Input
            id="emailSenderDomain"
            value={domain}
            placeholder="mail.youragency.com"
            spellCheck={false}
            onChange={(e) => setDomain(e.target.value)}
          />
          <Button
            type="button"
            disabled={busy || !domainDirty}
            onClick={() =>
              saveMutation.mutate({
                customEmailDomain: trimmedDomain.length > 0 ? trimmedDomain : null,
              })
            }
          >
            {saveMutation.isPending ? "Saving…" : hasDomain && !trimmedDomain ? "Remove" : "Save"}
          </Button>
        </div>
        <p className="text-[12px] text-muted-foreground">
          Reports will be sent from <code>reports@your-domain</code> once verified.
          Until then we send from the default ProvenLeap address.
        </p>
      </div>

      {/* DNS records to publish */}
      {hasDomain && data.dnsRecords.length > 0 && status !== "verified" ? (
        <div className="space-y-2 rounded-2xl border-[0.5px] border-[var(--hairline)] p-3">
          <p className="text-[12px] font-medium">
            Add these CNAME records at your DNS provider to verify ownership (DKIM):
          </p>
          <div className="space-y-2">
            {data.dnsRecords.map((rec) => (
              <div
                key={rec.name}
                data-testid="dns-record"
                className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] font-mono break-all"
              >
                <span className="text-muted-foreground">Type</span>
                <span>{rec.type}</span>
                <span className="text-muted-foreground">Name</span>
                <span>{rec.name}</span>
                <span className="text-muted-foreground">Value</span>
                <span>{rec.value}</span>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => verifyMutation.mutate()}
          >
            {verifyMutation.isPending ? "Checking…" : "Check verification"}
          </Button>
        </div>
      ) : null}

      {/* Reply-to support email */}
      <div className="space-y-2">
        <Label htmlFor="supportEmail">Reply-to support email</Label>
        <div className="flex items-center gap-2">
          <Input
            id="supportEmail"
            type="email"
            value={supportEmail}
            placeholder="help@youragency.com"
            spellCheck={false}
            onChange={(e) => setSupportEmail(e.target.value)}
          />
          <Button
            type="button"
            disabled={busy || !supportDirty}
            onClick={() =>
              saveMutation.mutate({
                supportEmail: supportEmail.trim().length > 0 ? supportEmail.trim() : null,
              })
            }
          >
            Save
          </Button>
        </div>
        <p className="text-[12px] text-muted-foreground">
          Clients replying to a report email reach this address. Works without a
          verified sender domain.
        </p>
      </div>

      {error ? (
        <p role="alert" className="text-[12px] text-red-900">
          {error}
        </p>
      ) : null}
      {notice && !error ? (
        <p role="status" className="text-[12px] text-emerald-900">
          {notice}
        </p>
      ) : null}
    </div>
  );
}
